import { Button } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";

import styles from "./LoginButton.module.css";
import { appServicesLogout, checkLoggedIn, getUsername, getTokenClaims, isInIframe } from "../../authConfig";
import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { LoginContext } from "../../loginContext";
import { useOidc } from "../../oidcContext";

export const LoginButton = () => {
    const { userManager } = useOidc();
    const { loggedIn, setLoggedIn } = useContext(LoginContext);
    const [username, setUsername] = useState("");
    const [claims, setClaims] = useState<Record<string, unknown> | undefined>(undefined);
    const [showClaims, setShowClaims] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        const fetchUserInfo = async () => {
            setUsername((await getUsername(userManager)) ?? "");
            setClaims(await getTokenClaims(userManager));
        };

        fetchUserInfo();
    }, [userManager, loggedIn]);

    // Listen for postMessage from popup-callback.html (login tab)
    // BroadcastChannel doesn't work here because browsers partition it by
    // top-level origin — the iframe's partition differs from the popup tab's.
    // postMessage via window.opener is not subject to this partitioning.
    useEffect(() => {
        if (!userManager) return;
        const onMessage = async (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== "login-complete" || !event.data.url) return;
            try {
                console.log("postMessage received login-complete, calling signinCallback");
                const user = await userManager.signinCallback(event.data.url);
                console.log("signinCallback succeeded, user:", user?.profile?.preferred_username);
                setLoggedIn(true);
            } catch (e: any) {
                console.error("signinCallback failed:", e?.message, e?.response);
            }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [userManager, setLoggedIn]);

    const handleLogin = useCallback(async () => {
        if (!userManager) return;

        if (isInIframe()) {
            // Open the popup/tab SYNCHRONOUSLY during the click event so the
            // browser's user-activation context is still valid.  If we await
            // anything before window.open(), the gesture is consumed and the
            // browser silently blocks the popup — especially in cross-origin iframes.
            // Use a named window so popup-callback.html can detect it should
            // use BroadcastChannel instead of signinPopupCallback()
            const loginWindow = window.open("about:blank", "nextchat-login");
            if (!loginWindow) {
                console.error("Browser blocked window.open – cannot log in from iframe");
                return;
            }
            try {
                const popupRedirectUri = window.location.origin + "/popup-callback.html";
                const oidcClient = (userManager as any)._client;
                const req = await oidcClient.createSigninRequest({
                    redirect_uri: popupRedirectUri,
                    request_type: "si:r"
                });
                // Navigate the already-open window to the auth URL
                loginWindow.location.href = req.url;
            } catch (e) {
                console.error("Failed to build login URL:", e);
                loginWindow.close();
            }
        } else {
            userManager.signinRedirect().catch(error => console.error(error));
        }
    }, [userManager, setLoggedIn]);

    const handleLogout = useCallback(() => {
        if (userManager) {
            if (isInIframe()) {
                userManager
                    .signoutPopup()
                    .then(() => setLoggedIn(false))
                    .catch(error => console.error(error));
            } else {
                userManager.signoutRedirect().catch(error => console.error(error));
            }
        } else {
            appServicesLogout();
        }
    }, [userManager, setLoggedIn]);

    const userInfo = useMemo(() => {
        if (!claims) return [];
        const keys = ["name", "given_name", "family_name", "email", "preferred_username", "department"] as const;
        return keys.filter(k => claims[k] != null && String(claims[k]).trim() !== "").map(k => ({ label: k, value: String(claims[k]) }));
    }, [claims]);

    return (
        <div style={{ position: "relative" }}>
            <Button className={styles.loginButton} onClick={loggedIn ? handleLogout : handleLogin}>
                {loggedIn ? `${t("logout")}\n${username}` : `${t("login")}`}
            </Button>
            {loggedIn && (
                <button className={styles.claimsToggle} onClick={() => setShowClaims(!showClaims)} title="User info">
                    ℹ️
                </button>
            )}
            {showClaims && userInfo.length > 0 && (
                <div className={styles.claimsPanel}>
                    <div className={styles.claimsPanelHeader}>
                        <strong>User Info</strong>
                    </div>
                    <table className={styles.claimsTable}>
                        <tbody>
                            {userInfo.map(({ label, value }) => (
                                <tr key={label}>
                                    <td className={styles.claimKey}>{label}</td>
                                    <td className={styles.claimValue}>{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
